import * as THREE from 'three';

/**
 * Blood Particle System
 * Creates gruesome blood effects for the game
 */
export class BloodParticleSystem {
    constructor(scene) {
        this.scene = scene;
        this.particles = [];
        this.bloodDecals = []; // Persistent blood decals on ground
    }
    
    /**
     * Create blood splatter at position
     * @param {THREE.Vector3} position - Impact position
     * @param {THREE.Vector3} direction - Direction of impact
     * @param {number} intensity - Intensity of splatter (1-10)
     */
    createBloodSplatter(position, direction, intensity = 5) {
        const particleCount = intensity * 20;
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const velocities = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        
        // Blood color variations (dark red to bright red)
        const bloodColors = [
            new THREE.Color(0x8B0000), // Dark red
            new THREE.Color(0xA00000), // Darker red
            new THREE.Color(0xCC0000), // Red
            new THREE.Color(0xFF0000), // Bright red
            new THREE.Color(0xDC143C)  // Crimson
        ];
        
        for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;
            
            // Position
            positions[i3] = position.x;
            positions[i3 + 1] = position.y;
            positions[i3 + 2] = position.z;
            
            // Velocity (spray in direction with random spread)
            const spread = 0.5;
            const speed = 5 + Math.random() * 10;
            const angle1 = (Math.random() - 0.5) * spread;
            const angle2 = (Math.random() - 0.5) * spread;
            
            velocities[i3] = direction.x * speed + angle1;
            velocities[i3 + 1] = direction.y * speed + Math.random() * 3;
            velocities[i3 + 2] = direction.z * speed + angle2;
            
            // Color (random blood color)
            const color = bloodColors[Math.floor(Math.random() * bloodColors.length)];
            colors[i3] = color.r;
            colors[i3 + 1] = color.g;
            colors[i3 + 2] = color.b;
            
            // Size
            sizes[i] = 0.1 + Math.random() * 0.2;
        }
        
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
        
        const material = new THREE.PointsMaterial({
            size: 0.2,
            vertexColors: true,
            transparent: true,
            opacity: 0.9,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });
        
        const particles = new THREE.Points(geometry, material);
        this.scene.add(particles);
        
        // Animate particles
        const particleData = {
            mesh: particles,
            velocities: velocities,
            life: 1.0,
            decayRate: 0.02
        };
        
        this.particles.push(particleData);
        
        return particles;
    }
    
    /**
     * Create blood trail (for wounded enemies)
     * @param {THREE.Vector3} start - Start position
     * @param {THREE.Vector3} end - End position
     */
    createBloodTrail(start, end) {
        const trailLength = start.distanceTo(end);
        const segmentCount = Math.floor(trailLength * 10);
        
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(segmentCount * 3);
        const colors = new Float32Array(segmentCount * 3);
        
        for (let i = 0; i < segmentCount; i++) {
            const t = i / segmentCount;
            const pos = new THREE.Vector3().lerpVectors(start, end, t);
            const i3 = i * 3;
            
            positions[i3] = pos.x;
            positions[i3 + 1] = pos.y + Math.random() * 0.1;
            positions[i3 + 2] = pos.z;
            
            // Dark red color
            colors[i3] = 0.55; // 0x8B0000
            colors[i3 + 1] = 0.0;
            colors[i3 + 2] = 0.0;
        }
        
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        
        const material = new THREE.LineBasicMaterial({
            vertexColors: true,
            linewidth: 2,
            transparent: true,
            opacity: 0.7
        });
        
        const trail = new THREE.Line(geometry, material);
        this.scene.add(trail);
        
        // Fade out over time
        setTimeout(() => {
            const fadeOut = () => {
                if (material.opacity > 0) {
                    material.opacity -= 0.05;
                    requestAnimationFrame(fadeOut);
                } else {
                    this.scene.remove(trail);
                    trail.geometry.dispose();
                    trail.material.dispose();
                }
            };
            fadeOut();
        }, 2000);
        
        return trail;
    }
    
    /**
     * Create blood decal on surface
     * @param {THREE.Vector3} position - Impact position
     * @param {THREE.Vector3} normal - Surface normal
     * @param {number} size - Size of decal
     */
    createBloodDecal(position, normal, size = 1) {
        // Create persistent blood decal that stays on ground
        const decalSize = size * (0.8 + Math.random() * 0.4); // Random size variation
        const decalGeometry = new THREE.PlaneGeometry(decalSize, decalSize);
        
        // Create more realistic blood material
        const decalMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color().setHSL(0, 0.8, 0.2), // Dark red
            transparent: true,
            opacity: 0.9,
            roughness: 0.9,
            metalness: 0.1,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        
        const decal = new THREE.Mesh(decalGeometry, decalMaterial);
        decal.position.copy(position);
        decal.position.y = 0.01; // Slightly above ground to prevent z-fighting
        decal.lookAt(position.clone().add(normal));
        decal.rotateX(Math.PI / 2);
        
        // Random rotation for variety
        decal.rotation.z = Math.random() * Math.PI * 2;
        
        this.scene.add(decal);
        this.bloodDecals.push(decal); // Store for persistence
        
        // Blood decals stay forever (or until scene is cleared)
        return decal;
    }
    
    /**
     * Create blood pool on ground (larger, more realistic)
     */
    createBloodPool(position, size = 2) {
        const poolGeometry = new THREE.CircleGeometry(size, 16);
        const poolMaterial = new THREE.MeshStandardMaterial({
            color: new THREE.Color().setHSL(0, 0.9, 0.15), // Very dark red
            transparent: true,
            opacity: 0.85,
            roughness: 0.95,
            metalness: 0.05,
            side: THREE.DoubleSide,
            depthWrite: false
        });
        
        const pool = new THREE.Mesh(poolGeometry, poolMaterial);
        pool.position.copy(position);
        pool.position.y = 0.01;
        pool.rotation.x = -Math.PI / 2;
        
        this.scene.add(pool);
        this.bloodDecals.push(pool);
        
        return pool;
    }
    
    /**
     * Update all particles
     * @param {number} deltaTime - Time since last frame
     */
    update(deltaTime) {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const particle = this.particles[i];
            particle.life -= particle.decayRate * deltaTime * 60;
            
            if (particle.life <= 0) {
                this.scene.remove(particle.mesh);
                particle.mesh.geometry.dispose();
                particle.mesh.material.dispose();
                this.particles.splice(i, 1);
                continue;
            }
            
            // Update positions
            const positions = particle.mesh.geometry.attributes.position.array;
            const velocities = particle.velocities;
            const gravity = -9.82;
            
            for (let j = 0; j < positions.length; j += 3) {
                // Update velocity (gravity)
                velocities[j + 1] += gravity * deltaTime;
                
                // Update position
                positions[j] += velocities[j] * deltaTime;
                positions[j + 1] += velocities[j + 1] * deltaTime;
                positions[j + 2] += velocities[j + 2] * deltaTime;
                
                // Stop on ground and create blood decal
                if (positions[j + 1] < 0.01) {
                    positions[j + 1] = 0.01;
                    if (velocities[j + 1] < -1 && Math.random() < 0.1) { // 10% chance to create decal
                        // Create small blood decal where particle hits ground
                        const decalPos = new THREE.Vector3(positions[j], 0.01, positions[j + 2]);
                        this.createBloodDecal(decalPos, new THREE.Vector3(0, 1, 0), 0.1 + Math.random() * 0.1);
                    }
                    velocities[j + 1] = 0;
                    velocities[j] *= 0.5; // Friction
                    velocities[j + 2] *= 0.5;
                }
            }
            
            particle.mesh.geometry.attributes.position.needsUpdate = true;
            
            // Fade out
            particle.mesh.material.opacity = particle.life;
        }
    }
}

